import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { useGetDormantWorkerList } from '@nxsys/hooks/api/report';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetDormantWorkerListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { formatFullName } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { ExportToCsv } from 'export-to-csv';
import { debounce, filter, isEmpty } from 'lodash';
import DormantWorkerReportFilterModal, {
  DormantWorkerReportFilterValues,
} from '../components/DormantWorkerFilterModal';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDormantWorkerColumns } from './columns';
import {
  useSubmitIssueP45,
  useValidateIssueP45,
} from '@nxsys/hooks/api/subcontractor';
import SubmitIssueP45Modal, {
  SubmitIssueP45Values,
} from '../components/SubmitIssueP45Modal';

const DormantWorkerReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { user, isBureau } = useSelector((state: AppState) => state.appStore);
  const { dormantWorkerList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = dormantWorkerList;

  const [selectedRows, setSelectedRows] = useState<IDormantWorker[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const {
    run: getDormantWorkerReportList,
    loading: isGetDormantWorkerReportListLoading,
  } = useGetDormantWorkerList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.dormantWorkerReport']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.issueP45']}
          variant="outlined"
          onClick={handleIssueP45Selected}
        />
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetDormantWorkerListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(reportActions.getDormantWorkerList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      dormantWeek: 4,
    });
  }, []);

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: DormantWorkerReportFilterValues) => {
    const params: GetDormantWorkerListParams = {
      ...queryParams,
      payFrequency: values.payFrequency,
      dormantWeek: values.dormantWeek,
      lastPaidFrom: values.lastPaidFrom,
      lastPaidTo: values.lastPaidTo,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  useNotificationSocket({
    eventName: `send-mail-issue-p45-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.issueP45SendCompleted'],
      });
      refetchData();
    },
  });
  const { run: validateIssueP45, loading: isValidateIssueP45Loading } =
    useValidateIssueP45();
  const [isOpenSubmitIssueP45Modal, setIsOpenSubmitIssueP45Modal] =
    useState(false);
  const [selectedItemToSubmitIssueP45, setSelectedItemToSubmitIssueP45] =
    useState<IDormantWorker[]>([]);
  const [p45DefaultDate, setP45DefaultDate] = useState<Moment | null>(null);
  const { run: submitIssueP45, loading: isSubmitIssueP45Loading } =
    useSubmitIssueP45();
  const handleIssueP45Multiple = (data: IDormantWorker[]) => {
    // NX-5493: match the classic UI — pending pension refunds block bulk P45
    // the same way an outstanding HP/ER pot does.
    const rowHaveHp = data.filter(
      (rowKey) =>
        rowKey?.setting?.haveOutstandingHolidayPay ||
        rowKey?.optOutState?.isRefundPension,
    );
    if (!isEmpty(rowHaveHp)) {
      showMessage({
        type: 'warning',
        content: messages['common.highlightedWorkersHaveOutstandingHpBalance'],
      });
      return;
    }
    if (rowHaveHp?.length === data?.length) {
      return;
    }
    const validRecord = data.filter(
      (item) =>
        !item.setting?.haveOutstandingHolidayPay &&
        !item?.optOutState?.isRefundPension,
    );
    validateIssueP45({ ids: validRecord.map((item) => item.id) }).then(
      (res) => {
        if (!res.ids?.length) {
          showMessage({
            type: 'error',
            content: messages['report.dormant.validateIssueP45Error'],
          });
        } else {
          // Default leaving date to latest payment date across selected workers
          if (res.lastestPaymentDate) {
            setP45DefaultDate(moment(res.lastestPaymentDate));
          }
          setIsOpenSubmitIssueP45Modal(true);
          setSelectedItemToSubmitIssueP45(validRecord);
        }
      },
    );
  };
  const handleIssueP45Selected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleIssueP45Multiple(selectedRows);
  };
  const handleSubmitIssueP45 = (values: SubmitIssueP45Values) => {
    submitIssueP45({
      ids: selectedItemToSubmitIssueP45.map((item) => item.id),
      dateLeft: moment(values.dateLeft).toISOString(),
      reason: values.reason?.value,
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'info',
          content: messages['common.issueP45SendPending'],
        });
        setSelectedRows([]);
        setSelectedItemToSubmitIssueP45([]);
      }
    });
  };

  const handleExport = (data: IDormantWorker[]) => {
    const dataExport = data.map((item) => {
      const {
        id,
        fullName,
        forename,
        surname,
        agencyRef,
        systemId,
        payFrequency,
        department,
        dateOfJoining,
        primaryAgency,
        companyPayment,
        previousPayroll,
      } = item;
      return {
        id,
        name: formatFullName({ fullName, forename, surname }),
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${
          agencyRef || systemId || ''
        }`,
        agencyName: primaryAgency?.name || '',
        payFrequency: filter(
          defaultPaymentFrequency,
          (item) => item?.value === payFrequency,
        )
          ? filter(
              defaultPaymentFrequency,
              (item) => item?.value === payFrequency,
            )[0]?.label
          : '',
        paymentCompany: companyPayment?.companyName || '',
        department: department || '',
        dateOfJoining: dateOfJoining
          ? moment(dateOfJoining).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        periodLastPaid: previousPayroll?.taxPeriod || '',
        dateLastPaid: previousPayroll?.paymentDate
          ? moment(previousPayroll?.paymentDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '',
      };
    });

    const headers = [
      'Employee ID',
      'Name',
      'Agency Reference',
      'Agency Name',
      'Pay Frequency',
      'Payment Company',
      'Department',
      'Date of Joining',
      'Period Last Paid',
      'Date Last Paid',
    ];

    const csvExporter = new ExportToCsv({
      filename: `Dormant-worker-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };
  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };
  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getDormantWorkerReportList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const getRowClassName = (record: any) => {
    if (
      record?.setting?.haveOutstandingHolidayPay ||
      record?.optOutState?.isRefundPension
    ) {
      return 'row-warning';
    }

    return '';
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.payFrequency ||
    !!queryParams?.dormantWeek ||
    !!queryParams?.lastPaidFrom ||
    !!queryParams?.lastPaidTo;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isGetDormantWorkerReportListLoading ||
        isValidateIssueP45Loading ||
        isSubmitIssueP45Loading
      }
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id_index"
            data={
              list?.map((item, index) => ({ ...item, id_index: index })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2500, y: 700 }}
            columns={getDormantWorkerColumns({
              messages,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            rowClassName={getRowClassName}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <DormantWorkerReportFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
      <SubmitIssueP45Modal
        isOpen={isOpenSubmitIssueP45Modal}
        toggle={() => setIsOpenSubmitIssueP45Modal(false)}
        onSubmit={handleSubmitIssueP45}
        defaultDate={p45DefaultDate}
      />
    </AppsContainer>
  );
};

export default DormantWorkerReport;
